import api from '@/services/api';
import type { ApiResponse } from '@/types';

export interface TenantInfo {
  id: string;
  name: string;
  subdomain: string;
  custom_domain?: string;
  plan_type: 'basic' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'deleted';
  branding_config?: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    favicon?: string;
  };
  feature_flags?: Record<string, boolean>;
  resource_limits?: {
    storage_gb: number;
    users: number;
    api_calls_per_month: number;
  };
}

export interface TenantContext {
  tenant: TenantInfo | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Cache for tenant info to avoid repeated API calls
const tenantCache = new Map<string, { data: TenantInfo; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Detects tenant from current URL
 * Supports both subdomain and custom domain detection
 */
export function detectTenantFromUrl(): { type: 'subdomain' | 'custom_domain' | 'admin'; value: string | null } {
  if (typeof window === 'undefined') {
    return { type: 'admin', value: null };
  }

  const hostname = window.location.hostname;
  
  // Check if it's localhost or admin domain
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('admin.cosmkdrm.com')) {
    return { type: 'admin', value: null };
  }

  // Check for subdomain pattern (tenant.cosmkdrm.com)
  const subdomainMatch = hostname.match(/^([a-z0-9-]+)\.cosmkdrm\.com$/);
  if (subdomainMatch) {
    return { type: 'subdomain', value: subdomainMatch[1] };
  }

  // For custom domains, we'll need to validate against the database
  return { type: 'custom_domain', value: hostname };
}

/**
 * Validates and fetches tenant information by subdomain
 */
export async function validateTenantSubdomain(subdomain: string): Promise<TenantInfo | null> {
  try {
    // Check cache first
    const cached = tenantCache.get(`subdomain:${subdomain}`);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const response = await api.get<ApiResponse<TenantInfo>>(`/tenants/by-subdomain/${subdomain}`);
    
    if (!response.success || !response.data) {
      console.warn('Tenant subdomain validation failed:', response.error);
      return null;
    }
    
    const tenantData = response.data;

    // Cache the result
    tenantCache.set(`subdomain:${subdomain}`, {
      data: tenantData,
      timestamp: Date.now()
    });

    return tenantData;
  } catch (error) {
    console.error('Error validating tenant subdomain:', error);
    return null;
  }
}

/**
 * Validates and fetches tenant information by custom domain
 */
export async function validateTenantCustomDomain(domain: string): Promise<TenantInfo | null> {
  try {
    // Check cache first
    const cached = tenantCache.get(`domain:${domain}`);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const response = await api.get<ApiResponse<TenantInfo>>(`/tenants/by-domain/${domain}`);
    
    if (!response.success || !response.data) {
      console.warn('Tenant domain validation failed:', response.error);
      return null;
    }
    
    const tenantData = response.data;

    // Cache the result
    tenantCache.set(`domain:${domain}`, {
      data: tenantData,
      timestamp: Date.now()
    });

    return tenantData;
  } catch (error) {
    console.error('Error validating tenant domain:', error);
    return null;
  }
}

/**
 * Resolves tenant information from current URL
 */
export async function resolveTenantFromUrl(): Promise<TenantInfo | null> {
  const detection = detectTenantFromUrl();
  
  if (detection.type === 'admin' || !detection.value) {
    return null;
  }

  if (detection.type === 'subdomain') {
    return await validateTenantSubdomain(detection.value);
  }

  if (detection.type === 'custom_domain') {
    return await validateTenantCustomDomain(detection.value);
  }

  return null;
}

/**
 * Gets tenant information by ID (for admin purposes)
 */
export async function getTenantById(tenantId: string): Promise<TenantInfo | null> {
  try {
    // Check cache first
    const cached = tenantCache.get(`id:${tenantId}`);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    const response = await api.get<ApiResponse<TenantInfo>>(`/admin/tenants/${tenantId}`);
    const tenantData = response.data.data;

    // Cache the result
    tenantCache.set(`id:${tenantId}`, {
      data: tenantData,
      timestamp: Date.now()
    });

    return tenantData;
  } catch (error) {
    console.error('Error fetching tenant by ID:', error);
    return null;
  }
}

/**
 * Checks if current environment is multi-tenant
 */
export function isMultiTenantEnvironment(): boolean {
  const detection = detectTenantFromUrl();
  return detection.type !== 'admin';
}

/**
 * Gets the base URL for API calls based on tenant context
 */
export function getTenantAwareApiBaseUrl(tenant?: TenantInfo | null): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
  
  if (!tenant) {
    return baseUrl;
  }

  // For multi-tenant environments, include tenant context in API calls
  return `${baseUrl}/tenant/${tenant.id}`;
}

/**
 * Constructs tenant-specific URLs
 */
export function getTenantUrl(tenant: TenantInfo, path: string = ''): string {
  const protocol = window.location.protocol;
  let domain: string;

  if (tenant.custom_domain) {
    domain = tenant.custom_domain;
  } else {
    domain = `${tenant.subdomain}.cosmkdrm.com`;
  }

  return `${protocol}//${domain}${path}`;
}

/**
 * Checks if a feature is enabled for the current tenant
 */
export function isTenantFeatureEnabled(tenant: TenantInfo | null, feature: string): boolean {
  if (!tenant) {
    return false;
  }

  // Check feature flags first
  if (tenant.feature_flags && typeof tenant.feature_flags[feature] === 'boolean') {
    return tenant.feature_flags[feature];
  }

  // Fallback to plan-based features
  const planFeatures: Record<string, string[]> = {
    basic: ['customBranding'],
    pro: ['customBranding', 'advancedAnalytics', 'apiAccess', 'bulkUpload'],
    enterprise: ['customBranding', 'whiteLabel', 'customDomain', 'advancedAnalytics', 'apiAccess', 'bulkUpload', 'nftMinting', 'geoblocking']
  };

  return planFeatures[tenant.plan_type]?.includes(feature) || false;
}

/**
 * Gets resource usage percentage for a tenant
 */
export function getTenantResourceUsage(tenant: TenantInfo, resourceType: 'storage' | 'users' | 'api_calls'): number {
  if (!tenant.resource_limits) {
    return 0;
  }

  const limit = tenant.resource_limits[resourceType === 'api_calls' ? 'api_calls_per_month' : resourceType];
  
  // If unlimited (-1), return 0%
  if (limit === -1) {
    return 0;
  }

  // This would need to be populated with actual usage data from the API
  // For now, returning 0 as placeholder
  return 0;
}

/**
 * Clears tenant cache (useful for logout or tenant switching)
 */
export function clearTenantCache(): void {
  tenantCache.clear();
}

/**
 * Pre-loads tenant information for better performance
 */
export async function preloadTenantInfo(): Promise<TenantInfo | null> {
  try {
    const tenant = await resolveTenantFromUrl();
    if (tenant) {
      // Cache additional tenant data if needed
      return tenant;
    }
  } catch (error) {
    console.error('Error preloading tenant info:', error);
  }
  return null;
}

/**
 * Validates tenant status and availability
 */
export function validateTenantAccess(tenant: TenantInfo | null): {
  isValid: boolean;
  reason?: string;
} {
  if (!tenant) {
    return { isValid: false, reason: 'Tenant not found' };
  }

  if (tenant.status === 'suspended') {
    return { isValid: false, reason: 'Tenant is suspended' };
  }

  if (tenant.status === 'deleted') {
    return { isValid: false, reason: 'Tenant has been deleted' };
  }

  return { isValid: true };
}